import * as React from 'react';

import { Dispatch } from 'redux';
import { useHistory } from 'react-router';
import { connect, useDispatch } from 'react-redux';

import { Translate } from 'react-localize-redux';

import styled from 'styled-components';

import { merchReleaseInitiatedEvent } from '@webapp/utils/rudderstack/events';
import {
  getLocalStorageItem,
  setLocalStorageItem,
} from '@webapp/utils/localstorage';

import AppState from '@webapp/store/state';
import { LogoutUser } from '@webapp/store/auth/action-creators';
import { activeSummary } from '@webapp/store/release/selectors';
import {
  CleanRelease,
  StartRelease,
} from '@webapp/store/release/action-creators';
import { CallHistoryMethodAction, push } from 'connected-react-router';
import { ListReleasesSummary } from '@webapp/store/release/action-creators';
import {
  CleanMerchStore,
  ListArtistMerch,
} from '@webapp/store/merch/action-creators';
import { UpdateArtist } from '@webapp/store/artist/action-creators';

import { ReleaseListSummary } from '@common/types/release/release-list-summary';
import SimpleArtist from '@common/types/artist/simple-artist';
import { default as AchievementsType } from '@common/types/artist/achievements';

import { MerchProduct } from '@services/merch/types/api/merch-product';

import MotionOpacity from '@components/motion/motion-opacity';
import NewDashboardItem from '@components/dashboard/new-dashboard-item';
import Achievements from '@components/dashboard/achievements';
import DashboardCarousel, {
  DashboardCarouselItem,
} from '@components/dashboard/dashboard-carousel';
import HelpWidget from '@components/dashboard/help-widget';
import ArtistSetiingsWidget from '@components/dashboard/artist-settings-widget';
import EarningsWidget from '@components/dashboard/earnings-widget';
import AnalyticsWidget from '@components/dashboard/analytics-widget';
import { DashboardImage } from '@components/dashboard/new-dashboard-item/item-img';

import DraftsMenu from '@webapp/controllers/menus/drafts-menu';

// Images for carousel.
import BlogImg from './img/blog-slide.webp';
import CommunityImg from './img/community-slide.webp';
import { OpenMenu } from '../../../store/ui/action-creators';

interface NewDashboardGeneralPageProps {
  logout: () => LogoutUser;
  getSummary: () => ListReleasesSummary;
  className?: string;
  activeArtistId?: string;
  activeArtistDetail?: SimpleArtist;
  achievements?: AchievementsType[];
  activeArtistName?: string;
  isLoading: boolean;
  isMobile: boolean;
  isPlanFeature?: boolean;
  clientId: string;
  subscriptionId: string;
  releases: ReleaseListSummary['all'];
  products: MerchProduct[];
  listArtistMerch: (artistId: string) => ListArtistMerch;
  cleanMerchStore: () => CleanMerchStore;
  cleanRelease: () => CleanRelease;
  updateArtist: (artist: SimpleArtist) => UpdateArtist;
  goTo: (url: string) => CallHistoryMethodAction;
}

const mapStateToProps = (state: AppState) => ({
  activeArtistId: state.artist.active?.id,
  activeArtistDetail: state.artist.active,
  achievements: state.artist.achievements,
  isLoading: Object.values(state.ui.loading).some((v) => !!v),
  isMobile: state.ui.mobile,
  clientId: state.artist.active?.id || '',
  subscriptionId: state.subscription.current?.id || '',
  isPlanFeature:
    state.subscription.current?.plan.toLocaleLowerCase() === 'beginner',
  releases: activeSummary(state),
  products: state.merch.artistMerch,
});

const mapDispatchToProps = (dispatch: Dispatch) => ({
  getSummary: () => dispatch(new ListReleasesSummary()),
  logout: () => dispatch(new LogoutUser()),
  listArtistMerch: (artistId: string) =>
    dispatch(new ListArtistMerch(artistId)),
  cleanMerchStore: () => dispatch(new CleanMerchStore()),
  cleanRelease: () => dispatch(new CleanRelease()),
  updateArtist: (artist: SimpleArtist) => dispatch(new UpdateArtist(artist)),
  goTo: (url: string) => dispatch(push(url)),
});

export const NewDashboardGeneralPage = (
  props: NewDashboardGeneralPageProps
) => {
  const {
    getSummary,
    releases,
    activeArtistId,
    activeArtistDetail,
    listArtistMerch,
    cleanMerchStore,
    products,
    goTo,
  } = props;
  const [releaseImages, setReleaseImages] = React.useState<
    DashboardImage[] | undefined
  >(undefined);
  const [merchImages, setMerchImages] = React.useState<
    DashboardImage[] | undefined
  >(undefined);

  const history = useHistory();
  const dispatch = useDispatch();

  React.useEffect(() => {
    getSummary();
  }, [getSummary]);

  React.useEffect(() => {
    cleanMerchStore();
    localStorage.removeItem('Trac.PayAsYouGo');
    if (activeArtistId) {
      listArtistMerch(activeArtistId);
    }
  }, [activeArtistId, listArtistMerch, cleanMerchStore]);

  React.useEffect(() => {
    if (
      activeArtistId &&
      !activeArtistDetail?.isVerified &&
      !getLocalStorageItem(`verificationMenuShown-${activeArtistId}`)
    ) {
      dispatch(new OpenMenu('VERIFICATION_MENU'));
      setLocalStorageItem(`verificationMenuShown-${activeArtistId}`, true);
    }
  }, [activeArtistDetail, activeArtistId, dispatch]);

  React.useEffect(() => {
    let images: DashboardImage[] | undefined = undefined;
    const getImage = (images) => {
      const front = images?.find((img) => img.position === 'front')?.src;
      if (front) {
        return front;
      }
      const other = images?.find((img) => img.position === 'other')?.src;
      if (other) {
        return other;
      }
      return images?.find((img) => !!img.src)?.src;
    };
    if (products) {
      images = products
        .filter((item) => item.status === 'draft')
        .map((product, index) => {
          return {
            id: index,
            src: getImage(product?.sizes[0]?.images),
            alt: product.title,
          };
        });
    }
    if (images) {
      if (images.length > 0) {
        setMerchImages(images);
      }
    }
  }, [products]);

  React.useEffect(() => {
    let images: DashboardImage[] | undefined = undefined;
    if (releases) {
      images = releases.list
        .filter((r) => r.status === 'draft')
        .sort((a, b) => {
          if (a.previousReleaseDate && b.previousReleaseDate) {
            return new Date(a.previousReleaseDate) >
              new Date(b.previousReleaseDate)
              ? -1
              : 1;
          }
          return 0;
        })
        .map((release, index) => {
          return {
            id: index,
            src: release.cover.url,
            alt: release.title,
          };
        });
    }
    setReleaseImages(images);
  }, [releases]);

  const DashboardCarouselItems: DashboardCarouselItem[] = [
    {
      img: CommunityImg,
      title: 'DASHBOARD.CAROUSEL.COMMUNITY.TITLE',
      text: 'DASHBOARD.CAROUSEL.COMMUNITY.TEXT',
      link: 'https://trac.co/community',
    },
    {
      img: BlogImg,
      title: 'DASHBOARD.CAROUSEL.BLOG.TITLE',
      text: 'DASHBOARD.CAROUSEL.BLOG.TEXT',
      link: 'https://blog.trac.co/',
    },
  ];

  // click handlers for dashboard items
  const handleMerchClick = async () => {
    await merchReleaseInitiatedEvent({
      artist_id: activeArtistId,
      artist_name: activeArtistDetail?.name,
    });

    history.push('/merch');
  };

  const handleReleaseClick = () => {
    dispatch(new StartRelease());
    history.push('/release');
  };

  return props.activeArtistId ? (
    <MotionOpacity>
      <DashboardWrapper className={props.className}>
        <LeftColumn>
          <NewDashboardItem
            title={<Translate id="DASHBOARD.NEW_DASHBOARD.MUSIC.TITLE" />}
            subtitle={<Translate id="DASHBOARD.NEW_DASHBOARD.MUSIC.SUBTITLE" />}
            btnTxt={<Translate id="DASHBOARD.NEW_DASHBOARD.MUSIC.BTN" />}
            to={props.isPlanFeature ? undefined : '/release'}
            onClick={handleReleaseClick}
            images={releaseImages}
            imgComponent={
              <DraftsMenu itemType="release" images={releaseImages} />
            }
            clientId={props.clientId}
            subscriptionId={props.subscriptionId}
          />
          <NewDashboardItem
            title={<Translate id="DASHBOARD.NEW_DASHBOARD.MERCH.TITLE" />}
            subtitle={<Translate id="DASHBOARD.NEW_DASHBOARD.MERCH.SUBTITLE" />}
            btnTxt={<Translate id="DASHBOARD.NEW_DASHBOARD.MERCH.BTN" />}
            onClick={handleMerchClick}
            btnType="secondary-dark"
            images={
              props.products && props.products?.length > 0
                ? merchImages
                : undefined
            }
            imgComponent={
              <DraftsMenu
                itemType="merch"
                images={
                  props.products && props.products?.length > 0
                    ? merchImages
                    : undefined
                }
              />
            }
            clientId={props.clientId}
            subscriptionId={props.subscriptionId}
          />
          {!props.isMobile && <Achievements openDistribution={() => null} />}
        </LeftColumn>
        <RightColumn>
          <ArtistSetiingsWidget
            name={activeArtistDetail?.name}
            avatar={activeArtistDetail?.avatar}
            goToArtistSettings={goTo}
          />
          <EarningsWidget />
          <AnalyticsWidget artistId={activeArtistId} />
          {props.isMobile && (
            <Achievements
              className="achievements"
              openDistribution={() => null}
            />
          )}
          <DashboardCarousel items={DashboardCarouselItems} />
          <HelpWidget />
        </RightColumn>
      </DashboardWrapper>
    </MotionOpacity>
  ) : null;
};

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(NewDashboardGeneralPage);

const DashboardWrapper = styled.section`
  width: 100%;
  padding: 32px 24px;
  display: grid;
  grid-gap: 30px;

  ${({ theme }) => `@media screen and ${theme.device.laptopS}`} {
    grid-template-columns: repeat(24, 1fr);
    padding: 48px 24px;
    grid-gap: 0;
  }

  ${({ theme }) => `@media screen and ${theme.device.laptop}`} {
    grid-template-columns: repeat(24, 1fr);
    padding: 48px 0;
  }
`;

const LeftColumn = styled.div`
  display: grid;
  gap: 30px;
  grid-gap: 30px;

  ${({ theme }) => `@media screen and ${theme.device.tablet}`} {
    grid-column: span 13;
    gap: 40px;
    grid-gap: 40px;
  }

  ${({ theme }) => `@media screen and ${theme.device.laptop}`} {
    grid-column: 4 / 14;
  }
`;

const RightColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;

  .achievements {
    margin: 48px 0 20px;
  }

  ${({ theme }) => `@media screen and ${theme.device.tablet}`} {
    grid-column: 15 / 25;
    gap: 12px;

    .achievements {
      margin: 0;
    }
  }

  ${({ theme }) => `@media screen and ${theme.device.laptop}`} {
    grid-column: 15 / 22;
  }
`;
